/**
 * ServiceOrderModalHeader - Cabeçalho (48px) do modal de detalhes da OS.
 *
 * Replica o visual do TaskModalHeader: selo de situação, número + título
 * truncado, ações em botões "pill" (ação principal) e ícones com dica, e
 * menu compacto no celular. Quais transições aparecem é decidido pelo
 * coordenador (`getServiceOrderTransitions` + parâmetros do perfil); o
 * servidor revalida tudo em `change_service_order_status`.
 *
 * RELEASE: 2026-10-09 v1.0.0 - Versão inicial (padronização com Tarefas).
 * RELEASE: 2026-10-10 v1.1.0 - BR-OS-032: botão "Agenda" antes do Iniciar, para
 *            escolher entre iniciar sem agendamento ou agendar para um técnico.
 * RELEASE: 2026-10-10 v1.2.0 - Botão "Agenda" some quando já existe agendamento
 *            ativo (mesma regra da aba Agenda), evitando agendamento duplicado.
 */
import {
  CalendarPlus, CalendarX2, CheckCircle2, GitBranch, MoreVertical, Pencil, Play, RotateCcw, Send, Undo2, X, XCircle, type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { PriorityFlag } from '@/components/tasks/PriorityFlag';
import type { ServiceOrderTransition } from '@/hooks/useServiceOrderDetails';
import type { ServiceOrderDetails } from '@/hooks/useServiceOrderDetails';
import { ServiceOrderStatusBadge } from '../ServiceOrderPresentation';
import { SERVICE_ORDER_SCHEDULABLE } from '@/hooks/useServiceOrders';

/** Aparência de cada transição, espelhando os botões de Tarefas. */
interface ActionLook { icon: LucideIcon; pill?: string; iconClass?: string }
function lookFor(t: ServiceOrderTransition): ActionLook {
  switch (t.to) {
    case 'in_progress':
      if (t.label === 'Reabrir') return { icon: RotateCcw };
      if (t.needsNote) return { icon: Undo2, iconClass: 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/20' };
      return { icon: Play, pill: 'border-green-600/60 text-green-700 hover:text-green-800 hover:bg-green-50 hover:border-green-700 dark:text-green-500 dark:hover:text-green-400 dark:hover:bg-green-900/20' };
    case 'in_correction':
      if (t.label === 'Reabrir') return { icon: RotateCcw };
      return { icon: Undo2, iconClass: 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/20' };
    case 'awaiting_review':
      return { icon: Send, pill: 'border-blue-600/60 text-blue-700 hover:text-blue-800 hover:bg-blue-50 hover:border-blue-700 dark:text-blue-500 dark:hover:text-blue-400 dark:hover:bg-blue-900/20' };
    case 'completed':
      return { icon: CheckCircle2, pill: 'border-green-600/60 text-green-700 hover:text-green-800 hover:bg-green-50 hover:border-green-700 dark:text-green-500 dark:hover:text-green-400 dark:hover:bg-green-900/20' };
    case 'cancelled':
      return { icon: XCircle, iconClass: 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20' };
    case 'open':
      return { icon: CalendarX2 };
    default:
      return { icon: Play };
  }
}

interface Props {
  order: ServiceOrderDetails;
  transitions: ServiceOrderTransition[];
  busy?: boolean;
  canEdit: boolean;
  onTransition: (t: ServiceOrderTransition) => void;
  onEdit: () => void;
  onClose: () => void;
  /** BR-OS-021: abre o pop-up de troca de categoria/fase */
  onChangePhase?: () => void;
  /** BR-OS-032: abre a escolha entre iniciar sem agendamento ou agendar */
  onSchedule?: () => void;
  /** Indica que já existe agendamento ativo — oculta o botão Agenda (paridade com a aba Agenda) */
  hasActiveSchedule?: boolean;
}

export function ServiceOrderModalHeader({ order, transitions, busy, canEdit, onTransition, onEdit, onClose, onChangePhase, onSchedule, hasActiveSchedule }: Props) {
  const isMobile = useIsMobile();
  // BR-OS-032: botão Agenda visível com a OS Aberta/Agendada, permissão de edição
  // e SEM agendamento ativo (mesma regra do botão Agendar da aba Agenda)
  // O pai autoriza agenda/fase separadamente da edição dos campos.
  const showSchedule = !!onSchedule && !hasActiveSchedule
    && SERVICE_ORDER_SCHEDULABLE.includes(order.status);

  return (
    <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 h-12 border-b bg-background shrink-0">
      <ServiceOrderStatusBadge status={order.status} overdue={order.is_overdue} />
      <span className="text-xs md:text-sm font-semibold text-muted-foreground shrink-0">{order.number} —</span>
      <h2 className="flex min-w-0 flex-1 items-center gap-2 text-xs md:text-sm font-semibold">
        <PriorityFlag priority={order.priority as any} size={13} className="shrink-0" />
        <span className="truncate">{order.service_type_name}{order.customer_name ? ` · ${order.customer_name}` : ''}</span>
      </h2>

      {isMobile ? (
        /* Celular: menu compacto, igual Tarefas */
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {(transitions.length > 0 || canEdit || onChangePhase || showSchedule) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações"><MoreVertical className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                {transitions.map((t) => {
                  const L = lookFor(t);
                  return (
                    <DropdownMenuItem key={t.to + t.label} disabled={busy} onClick={() => onTransition(t)}
                      className={t.to === 'cancelled' ? 'text-destructive' : undefined}>
                      <L.icon className="mr-2 h-3.5 w-3.5" />{t.label}
                    </DropdownMenuItem>
                  );
                })}
                {showSchedule && <DropdownMenuItem onClick={onSchedule}><CalendarPlus className="mr-2 h-3.5 w-3.5" />Agenda</DropdownMenuItem>}
                {onChangePhase && <DropdownMenuItem onClick={onChangePhase}><GitBranch className="mr-2 h-3.5 w-3.5" />Alterar fase</DropdownMenuItem>}
                {canEdit && <DropdownMenuItem onClick={onEdit}><Pencil className="mr-2 h-3.5 w-3.5" />Editar</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted" onClick={onClose} aria-label="Fechar"><X className="h-4 w-4" /></Button>
        </div>
      ) : (
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {/* BR-OS-032: Agenda antes do Iniciar — iniciar sem agendamento ou agendar para um técnico */}
          {showSchedule && (
            <Button variant="outline" size="sm" disabled={busy} onClick={onSchedule}
              className="h-8 gap-1.5 border-primary/60 text-primary hover:bg-primary/10">
              <CalendarPlus className="h-3.5 w-3.5" /><span className="font-medium">Agenda</span>
            </Button>
          )}
          {transitions.map((t) => {
            const L = lookFor(t);
            // Ação principal em "pill" com texto; secundárias em ícone com dica
            if (L.pill) {
              return (
                <Button key={t.to + t.label} variant="outline" size="sm" disabled={busy}
                  className={cn('h-8 gap-1.5', L.pill)} onClick={() => onTransition(t)}>
                  <L.icon className="h-3.5 w-3.5" /><span className="font-medium">{t.label}</span>
                </Button>
              );
            }
            return (
              <Tooltip key={t.to + t.label}>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" disabled={busy} aria-label={t.label}
                    className={cn('h-8 w-8', L.iconClass)} onClick={() => onTransition(t)}>
                    <L.icon className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p className="text-xs">{t.label}</p></TooltipContent>
              </Tooltip>
            );
          })}
          <div className="mx-1 h-4 w-px bg-border" />
          {onChangePhase && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onChangePhase} aria-label="Alterar Fase"><GitBranch className="h-3.5 w-3.5" /></Button>
              </TooltipTrigger>
              <TooltipContent side="bottom"><p className="text-xs">Alterar Fase</p></TooltipContent>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} aria-label="Editar"><Pencil className="h-3.5 w-3.5" /></Button>
              </TooltipTrigger>
              <TooltipContent side="bottom"><p className="text-xs">Editar</p></TooltipContent>
            </Tooltip>
          )}
          <div className="mx-1 h-4 w-px bg-border" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted" onClick={onClose} aria-label="Fechar"><X className="h-4 w-4" /></Button>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p className="text-xs">Fechar</p></TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
